import React, { useState, forwardRef, useEffect } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { validateData, type ErrorObject } from '../../providers/validateData';
import CustomTypography from '../Typography/CustomTypography';

interface Option {
  name: string;
  id: string | number;
}

interface CustomSelectProps extends ValidationError {
  id: string;
  label?: string;
  className?: string;
  LabelClassName?: string;
  menuItemClassName?: string;
  options: Option[];
  selectedValue?: string | number;
  defaultValue?: string | number;
  onChange: (value: string | number) => void;
  size?: 'small' | 'medium';
  variant?: 'standard' | 'outlined' | 'filled';
  disabled?: boolean;
  fullWidth?: boolean;
  displayEmpty?: boolean;
  placeholder?: string;
  onOptionClick?: (value: string | number) => void;
  emptyOptionLabel?: string;
}

interface ValidationError {
  onValidationError?: (error: ErrorObject | null) => void;
  validateRules?: 'required'[];
}

const CustomSelect = forwardRef<HTMLDivElement, CustomSelectProps>(
  (
    {
      id,
      label,
      className,
      LabelClassName,
      menuItemClassName,
      options,
      selectedValue = '',
      defaultValue = '',
      onChange,
      size = 'medium',
      variant = 'outlined',
      disabled = false,
      fullWidth = false,
      displayEmpty = false,
      placeholder = '',
      validateRules,
      onValidationError,
      onOptionClick,
      emptyOptionLabel,
    },
    ref
  ) => {
    const [value, setValue] = useState<string | number>(selectedValue);
    const [inputError, setInputError] = React.useState<ErrorObject | null>(
      null
    );

    const handleChange = (event: SelectChangeEvent<string | number>) => {
      const newValue = event.target.value;
      setValue(newValue);
      onChange(newValue);
      if (validateRules) {
        const errors = validateData(event.target.value, validateRules);
        if (onValidationError) {
          onValidationError(errors);
        }
        setInputError(errors);
      }
    };

    useEffect(() => {
      setValue(selectedValue);
    }, [selectedValue]);
    return (
      <FormControl
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        variant={variant}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: disabled ? '#d4d4d4' : '',
          },
        }}
      >
        {label && <InputLabel className={LabelClassName}>{label}</InputLabel>}
        <Select
          labelId={`${id}-label`}
          id={id}
          className={className}
          value={value ?? ''}
          defaultValue={defaultValue}
          displayEmpty={displayEmpty}
          onChange={handleChange}
          label={label}
          ref={ref}
          renderValue={(selected) => {
            if (
              selected === 0 ||
              selected === '0' ||
              selected === '' ||
              selected === undefined ||
              selected === null
            ) {
              return (
                <span
                  style={{
                    color: '#848B90',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}
                >
                  {placeholder}
                </span>
              );
            }
            return (
              <span
                style={{
                  fontSize: '12px', // Change this to your desired font size
                  fontWeight: '500',
                  color: '#222222',
                }}
              >
                {options.find((opt) => opt.id === selected)?.name}
              </span>
            );
          }}
        >
          {options.length === 0 ? (
            <MenuItem className={menuItemClassName} disabled value="">
              {emptyOptionLabel ?? 'No Options Available'}
            </MenuItem>
          ) : (
            options.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                className={menuItemClassName}
                sx={{ color: '#222222', fontSize: '12px', fontWeight: '500' }}
                onClick={() => {
                  onOptionClick?.(option.id);
                }}
              >
                {option.name}
              </MenuItem>
            ))
          )}
        </Select>
        {inputError?.invalid && (
          <CustomTypography id="id-error" variant="h6" color="error">
            {inputError?.message}
          </CustomTypography>
        )}
      </FormControl>
    );
  }
);
CustomSelect.displayName = 'CustomSelect';
export default CustomSelect;
